import { useState, useEffect, useCallback, useRef } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { Button, Chip, Icon, Toast } from "@legal-500/legal500-design-system";
import type { Column, ToastType } from "@legal-500/legal500-design-system";
import { PageHeader } from "@/components/PageHeader";
import { PaginationControls } from "@/components/PaginationControls";
import EmptyState from "@/components/EmptyState";
import LoadingState from "@/components/LoadingState";
import { ErrorBanner } from "@/components/ErrorBanner/ErrorBanner";
import { DataTable } from "@/components/DataTable/DataTable";
import {
  mailingGroupsService,
  type MailingGroupCategory,
  type MailingGroupDetailDto,
} from "@/api/mailing-groups.service";
import {
  refereesService,
  type RefereeByRegion,
  type RefereeScope,
  type RefereesByRegionsResponse,
} from "@/api/referees.service";
import { campaignsService, type Campaign } from "@/api/campaigns.service";
import { ROUTES } from "@/routes";
import { StartCampaignModal } from "./StartCampaignModal";
import { AddReminderModal } from "./AddReminderModal";
import { campaignStatusChip, campaignTypeLabel } from "./campaign-display";
import styles from "./MailingGroups.module.css";

const ITEMS_PER_PAGE = 10;

// Statuses in which a group's latest campaign has not yet been sent. The backend
// rejects creating the next campaign (reminder) while the latest is in one of
// these states with 409 PREVIOUS_CAMPAIGN_NOT_SENT.
const UNSENT_STATUSES = ["draft", "synchronizing"];

// Shown as the disabled-button tooltip/hint and the 409 toast so the operator
// sees the same reason whether they hover the button or hit the race.
const PREVIOUS_CAMPAIGN_NOT_SENT_HINT = "Send the previous campaign before adding a reminder.";

/** Small label shown next to bar-group names; standard groups render nothing. */
function categoryLabel(category: MailingGroupCategory): string | null {
  if (category === "instructing_solicitor") return "Instructing solicitor";
  if (category === "other") return "Other";
  return null;
}

/**
 * Maps the group's category onto the `refereeCategory` filter value accepted by
 * the by-regions endpoint. The backend normalises the string (lowercase/trim),
 * so "Instructing Solicitor" buckets to instructing and "Other" buckets to any
 * non-instructing category. A standard group (null) passes no filter.
 */
function refereeCategoryFilter(category: MailingGroupCategory): string | undefined {
  if (category === "instructing_solicitor") return "Instructing Solicitor";
  if (category === "other") return "Other";
  return undefined;
}

/**
 * Formats a campaign's `created_at` (snake_case ISO string from the API) as a
 * locale date. Guards against an undefined/unparseable value so the list never
 * renders "Invalid Date".
 */
function formatCampaignDate(value: string | null | undefined): string {
  if (!value) return "—";
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? "—" : date.toLocaleDateString();
}

const refereeColumns: Column<RefereeByRegion>[] = [
  {
    header: "Name",
    accessor: "firstName",
    render: (_value: unknown, row: RefereeByRegion) =>
      [row.title, row.firstName, row.surname].filter(Boolean).join(" ").trim(),
  },
  {
    header: "Email",
    accessor: "emailAddress",
    render: (value: unknown) => (value === null || value === undefined ? "N/A" : String(value)),
  },
  {
    header: "Company",
    accessor: "companyName",
    render: (value: unknown) => (value === null || value === undefined ? "—" : String(value)),
  },
  {
    header: "Referee Category",
    accessor: "refereeCategory",
    render: (value: unknown) => (value === null || value === undefined ? "—" : String(value)),
  },
  {
    header: "Region",
    accessor: "regionName",
    render: (value: unknown) => (value === null || value === undefined ? "—" : String(value)),
  },
];

function MailingGroupView() {
  const {
    publicationId = "",
    edition = "",
    groupId = "",
  } = useParams<{ publicationId: string; edition: string; groupId: string }>();

  const navigate = useNavigate();

  const [group, setGroup] = useState<MailingGroupDetailDto | null>(null);
  const [groupLoading, setGroupLoading] = useState(true);
  const [groupError, setGroupError] = useState<string | null>(null);

  // --- Campaigns (primary section) ---
  const [campaigns, setCampaigns] = useState<Campaign[]>([]);
  const [campaignsLoading, setCampaignsLoading] = useState(false);
  const [campaignsError, setCampaignsError] = useState<string | null>(null);
  const [startModalOpen, setStartModalOpen] = useState(false);
  const [reminderModalOpen, setReminderModalOpen] = useState(false);
  // Whether an invitation was already sent to this group; drives the reminder
  // modal's "nobody invited yet" warning. `null` until the summary resolves.
  const [reminderInvitationSent, setReminderInvitationSent] = useState<boolean | null>(null);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [resyncingId, setResyncingId] = useState<string | null>(null);
  const [toast, setToast] = useState<{ message: string; type: ToastType } | null>(null);

  // --- Referee preview (secondary section) ---
  const [referees, setReferees] = useState<RefereesByRegionsResponse | null>(null);
  const [refereesLoading, setRefereesLoading] = useState(false);
  const [refereesError, setRefereesError] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState(1);

  const groupRefereeScopeRef = useRef<RefereeScope | null>(null);

  const loadGroup = useCallback(async () => {
    setGroupLoading(true);
    setGroupError(null);
    try {
      const { group: detail } = await mailingGroupsService.getById(groupId);
      groupRefereeScopeRef.current = detail.edition_id
        ? {
            editionId: detail.edition_id,
            rankingEntityIds: detail.regions.flatMap(r =>
              r.ranking_entity_id === null ? [] : [r.ranking_entity_id],
            ),
          }
        : {
            publicationId: Number(publicationId),
            regionIds: detail.regions.flatMap(r => (r.region_id === null ? [] : [r.region_id])),
          };
      setGroup(detail);
    } catch (err) {
      setGroupError(err instanceof Error ? err.message : "Failed to load mailing group.");
    } finally {
      setGroupLoading(false);
    }
  }, [groupId, publicationId]);

  const loadCampaigns = useCallback(async () => {
    setCampaignsLoading(true);
    setCampaignsError(null);
    try {
      const data = await campaignsService.listForGroup(groupId);
      setCampaigns(data);
    } catch (err) {
      setCampaignsError(err instanceof Error ? err.message : "Failed to load campaigns.");
    } finally {
      setCampaignsLoading(false);
    }
  }, [groupId]);

  const loadReferees = useCallback(async (category: MailingGroupCategory, page: number) => {
    const scope = groupRefereeScopeRef.current;
    if (!scope) return;
    setRefereesLoading(true);
    setRefereesError(null);
    try {
      const data = await refereesService.getRefereesByRegions({
        ...scope,
        refereeCategory: refereeCategoryFilter(category),
        limit: ITEMS_PER_PAGE,
        offset: (page - 1) * ITEMS_PER_PAGE,
      });
      setReferees(data);
    } catch (err) {
      setRefereesError(err instanceof Error ? err.message : "Failed to load referees.");
    } finally {
      setRefereesLoading(false);
    }
  }, []);

  useEffect(() => {
    void loadGroup();
  }, [loadGroup]);

  useEffect(() => {
    void loadCampaigns();
  }, [loadCampaigns]);

  // A freshly-created invitation starts as `synchronizing` while the API copies
  // recipients in the background, then flips to `draft`. Poll the list silently
  // while any campaign is synchronizing; stop once none are.
  const anySynchronizing = campaigns.some(c => c.status === "synchronizing");
  useEffect(() => {
    if (!anySynchronizing) return;
    let cancelled = false;
    const interval = setInterval(() => {
      campaignsService
        .listForGroup(groupId)
        .then(data => {
          if (!cancelled) setCampaigns(data);
        })
        .catch(() => {
          // Transient poll failure: keep the current list and retry next tick.
        });
    }, 2000);
    return () => {
      cancelled = true;
      clearInterval(interval);
    };
  }, [anySynchronizing, groupId]);

  // Once the group resolves, load the first referee page for its regions — but
  // only when the group has no campaigns yet (the recipients preview is a
  // pre-flight view that disappears once a campaign exists). Skip the heavy
  // by-regions query entirely while campaigns are loading/errored or present.
  const noCampaignsYet = !campaignsLoading && !campaignsError && campaigns.length === 0;
  useEffect(() => {
    if (!noCampaignsYet) return;
    if (group) {
      void loadReferees(group.category, currentPage);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [group, currentPage, noCampaignsYet]);

  // The Start Campaign and Add reminder modals hit the same endpoint; the
  // backend derives the invitation vs the next reminder from the existing
  // sequence, so both confirm handlers simply create + refresh.
  const handleCreateCampaign = useCallback(
    async (templateId?: string) => {
      try {
        await campaignsService.createForGroup(groupId, templateId ? { templateId } : undefined);
        setToast({ message: "Campaign created.", type: "success" });
        await loadCampaigns();
      } catch (err) {
        const status = (err as { status?: number }).status;
        const code = (err as { code?: string }).code;
        // The backend blocks adding a reminder while the latest campaign is still
        // unsent. The button is normally disabled for this, but a stale list can
        // race the guard — surface the specific reason and refetch the truth.
        if (status === 409 || code === "PREVIOUS_CAMPAIGN_NOT_SENT") {
          setToast({ message: PREVIOUS_CAMPAIGN_NOT_SENT_HINT, type: "error" });
          await loadCampaigns();
          return;
        }
        setToast({
          message: err instanceof Error ? err.message : "Failed to create campaign.",
          type: "error",
        });
      }
    },
    [groupId, loadCampaigns],
  );

  const handleDeleteCampaign = useCallback(async (campaign: Campaign) => {
    setDeletingId(campaign.id);
    try {
      await campaignsService.remove(campaign.id);
      setToast({ message: "Campaign deleted.", type: "success" });
      setCampaigns(prev => prev.filter(c => c.id !== campaign.id));
    } catch (err) {
      setToast({
        message: err instanceof Error ? err.message : "Failed to delete campaign.",
        type: "error",
      });
    } finally {
      setDeletingId(null);
    }
  }, []);

  const handleResyncCampaign = useCallback(async (campaign: Campaign) => {
    setResyncingId(campaign.id);
    try {
      const { status } = await campaignsService.resyncCampaign(campaign.id);
      setToast({
        message: status === "in_progress" ? "Sync already running" : "Sync restarted",
        type: "success",
      });
    } catch (err) {
      setToast({
        message: err instanceof Error ? err.message : "Failed to restart sync.",
        type: "error",
      });
    } finally {
      setResyncingId(null);
    }
  }, []);

  // When the reminder modal opens, fetch the group's response summary so the
  // modal can warn if no invitation has been sent yet. Failure leaves the flag
  // null (no warning) so the modal never hard-blocks on a summary error.
  useEffect(() => {
    if (!reminderModalOpen) return;
    let cancelled = false;
    setReminderInvitationSent(null);
    mailingGroupsService
      .getResponseSummary(groupId)
      .then(summary => {
        if (!cancelled) setReminderInvitationSent(summary.invitationSent);
      })
      .catch(() => {
        // Leave the flag null — the modal simply omits the warning.
      });
    return () => {
      cancelled = true;
    };
  }, [reminderModalOpen, groupId]);

  const handleRetryReferees = () => {
    if (group) void loadReferees(group.category, currentPage);
    else void loadGroup();
  };

  const breadcrumb = (
    <p className={styles.breadcrumb}>
      <Link
        to={ROUTES.surveys.campaignDetail(publicationId, edition)}
        className={styles.breadcrumbLink}
      >
        Back to groups
      </Link>
    </p>
  );

  if (groupLoading) {
    return (
      <div className={styles.wrapper}>
        {breadcrumb}
        <LoadingState message="Loading mailing group..." />
      </div>
    );
  }

  if (groupError || !group) {
    return (
      <div className={styles.wrapper}>
        {breadcrumb}
        <ErrorBanner
          error={groupError ?? "Mailing group not found."}
          onRetry={loadGroup}
          loading={groupLoading}
        />
      </div>
    );
  }

  const category = categoryLabel(group.category);

  // The "latest" campaign is the one with the highest sequence in the loaded
  // list (the invitation is seq 0, reminders climb from there). A reminder can
  // only be added once that latest campaign has actually been sent — mirrors the
  // backend's 409 PREVIOUS_CAMPAIGN_NOT_SENT guard.
  const latestCampaign = campaigns.reduce<Campaign | null>(
    (latest, c) => (latest === null || c.sequence > latest.sequence ? c : latest),
    null,
  );
  const canAddReminder =
    latestCampaign !== null && !UNSENT_STATUSES.includes(latestCampaign.effective_status);

  const refereesList = referees?.referees ?? [];
  const total = referees?.pagination.total ?? 0;
  const totalPages = Math.max(1, Math.ceil(total / ITEMS_PER_PAGE));

  const campaignColumns: Column<Campaign>[] = [
    { header: "Name", accessor: "name" },
    {
      header: "Type",
      accessor: "type",
      render: (_value, row) => campaignTypeLabel(row.type),
    },
    {
      header: "Status",
      accessor: "status",
      render: (_value, row) => {
        const chip = campaignStatusChip(row.effective_status);
        return (
          <Chip
            size="small"
            color={chip.color}
            className={chip.color === "default" ? styles.neutralStatusChip : undefined}
          >
            <span className={styles.statusChip}>
              <span
                className={styles.statusDot}
                style={{ backgroundColor: chip.dotColor }}
                aria-hidden="true"
              />
              {chip.label}
            </span>
          </Chip>
        );
      },
    },
    {
      header: "Targets",
      accessor: "recipients_total" as keyof Campaign,
      render: (_value, row) =>
        row.recipients_total > 0
          ? `${row.recipients_sent.toLocaleString()} / ${row.recipients_total.toLocaleString()}`
          : "—",
    },
    {
      header: "Created",
      accessor: "created_at" as keyof Campaign,
      render: (_value, row) => formatCampaignDate(row.created_at),
    },
    {
      header: "",
      accessor: "actions" as keyof Campaign,
      render: (_value, row) => (
        <div className={styles.rowActions}>
          {row.status === "synchronizing" && (
            // A survey invitation can get stuck `synchronizing` if its background
            // sync job was lost (e.g. a Redis restart); Retry sync re-drives it.
            <Button
              variant="outlined"
              color="primary"
              size="small"
              aria-label={`Retry sync ${row.name}`}
              disabled={resyncingId === row.id}
              onClick={event => {
                event.stopPropagation();
                void handleResyncCampaign(row);
              }}
            >
              Retry sync
            </Button>
          )}
          <Button
            variant="outlined"
            color="error"
            size="small"
            className={styles.actionDeleteButton}
            aria-label={`Delete ${row.name}`}
            disabled={deletingId === row.id || row.status === "synchronizing"}
            onClick={event => {
              // Keep row-click navigation from firing when deleting.
              event.stopPropagation();
              void handleDeleteCampaign(row);
            }}
          >
            <Icon icon="delete" size="small" />
          </Button>
        </div>
      ),
    },
  ];

  return (
    <div className={styles.wrapper}>
      <PageHeader title={group.name} subtitle={category ? `Category: ${category}` : undefined}>
        <Button
          variant="outlined"
          color="primary"
          size="small"
          startIcon={<Icon icon="chevron-left" size="small" />}
          onClick={() => navigate(ROUTES.surveys.campaignDetail(publicationId, edition))}
        >
          Back to groups
        </Button>
      </PageHeader>

      <p className={styles.breadcrumb}>
        <Link
          to={ROUTES.surveys.campaignDetail(publicationId, edition)}
          className={styles.breadcrumbLink}
        >
          Back to groups
        </Link>{" "}
        / {group.name}
        {category && (
          <>
            {" "}
            <Chip size="small" color="primary">
              {category}
            </Chip>
          </>
        )}
      </p>

      <section className={styles.campaignsSection}>
        <div className={styles.recipientsHeaderRow}>
          <h2 className={styles.sectionHeading}>Campaigns</h2>
          {noCampaignsYet ? (
            <div className={styles.campaignsActions}>
              <Button
                variant="contained"
                color="primary"
                size="small"
                startIcon={<Icon icon="plus" size="small" />}
                onClick={() => setStartModalOpen(true)}
              >
                Create invitation
              </Button>
            </div>
          ) : (
            campaigns.length > 0 && (
              <div className={styles.campaignsActions}>
                {!canAddReminder && (
                  <span className={styles.mutedNote}>{PREVIOUS_CAMPAIGN_NOT_SENT_HINT}</span>
                )}
                <Button
                  variant="contained"
                  color="primary"
                  size="small"
                  startIcon={<Icon icon="plus" size="small" />}
                  disabled={!canAddReminder}
                  title={canAddReminder ? undefined : PREVIOUS_CAMPAIGN_NOT_SENT_HINT}
                  onClick={() => setReminderModalOpen(true)}
                >
                  Add reminder
                </Button>
              </div>
            )
          )}
        </div>

        {campaignsError && (
          <ErrorBanner error={campaignsError} onRetry={loadCampaigns} loading={campaignsLoading} />
        )}

        {campaignsLoading && campaigns.length === 0 ? (
          <LoadingState message="Loading campaigns..." />
        ) : !campaignsError && campaigns.length === 0 ? (
          /* The "Create invitation" trigger lives in the header (a single button
             on the page so the e2e's by-name lookup is unambiguous); this compact
             notice just points the user at it — deliberately not the tall shared
             EmptyState, which overwhelms this section. */
          <div className={styles.noCampaignsNotice}>
            <Icon icon="info" size="small" aria-hidden="true" />
            <div className={styles.noCampaignsText}>
              <span className={styles.noCampaignsTitle}>No campaign started yet</span>
              <span>
                No survey invitation has been sent to this group yet. Review the potential
                recipients below to check the list looks right, then use Create invitation above to
                send it.
              </span>
            </div>
          </div>
        ) : (
          !campaignsError && (
            <div className={styles.tableContainer}>
              <DataTable
                data={campaigns}
                columns={campaignColumns}
                size="small"
                rowKey="id"
                onRowClick={row =>
                  navigate(
                    ROUTES.surveys.campaignDetailPage(publicationId, edition, groupId, row.id),
                  )
                }
              />
            </div>
          )
        )}
      </section>

      {/* Pre-flight recipients preview: only relevant before any campaign is
          started. Gated on !campaignsLoading (not just campaigns.length === 0)
          so it never flashes on first paint before campaigns resolve. */}
      {noCampaignsYet && (
        <section className={styles.recipientsPreviewSection}>
          <div className={styles.recipientsSectionHeader}>
            <h2 className={styles.sectionHeading}>Potential recipients</h2>
            <span className={styles.recipientsCount}>
              <span className={styles.recipientsCountLabel}>Unique emails</span>
              <span className={styles.recipientsCountValue}>{referees?.uniqueEmails ?? 0}</span>
            </span>
          </div>

          {refereesError && (
            <ErrorBanner
              error={refereesError}
              onRetry={handleRetryReferees}
              loading={refereesLoading}
            />
          )}

          {refereesLoading && referees === null ? (
            <LoadingState message="Loading referees..." />
          ) : !refereesError && refereesList.length === 0 && total === 0 ? (
            <EmptyState
              variant="info"
              title="No referees"
              message="No referees match the regions in this group."
            />
          ) : (
            !refereesError && (
              <>
                <div className={styles.tableContainer}>
                  <DataTable
                    data={refereesList}
                    columns={refereeColumns}
                    size="small"
                    rowKey="id"
                  />
                </div>
                <PaginationControls
                  currentPage={currentPage}
                  totalPages={totalPages}
                  startIndex={(currentPage - 1) * ITEMS_PER_PAGE}
                  endIndex={(currentPage - 1) * ITEMS_PER_PAGE + refereesList.length}
                  totalCount={total}
                  itemName="referees"
                  onPageChange={setCurrentPage}
                />
              </>
            )
          )}
        </section>
      )}

      <StartCampaignModal
        isOpen={startModalOpen}
        onClose={() => setStartModalOpen(false)}
        onConfirm={handleCreateCampaign}
      />
      <AddReminderModal
        isOpen={reminderModalOpen}
        invitationSent={reminderInvitationSent}
        onClose={() => setReminderModalOpen(false)}
        onConfirm={handleCreateCampaign}
      />

      {toast && (
        <Toast
          message={toast.message}
          type={toast.type}
          duration={toast.type === "error" ? 5000 : 3000}
          onClose={() => setToast(null)}
        />
      )}
    </div>
  );
}

export default MailingGroupView;
